<template>
  <div class="html-wysiwyg-editor">
    <div class="editor-header">
      <div class="header-left">
        <b-button variant="link" @click="goBack" class="back-button">
          <i class="fa fa-arrow-left"></i>
        </b-button>
        <h4 class="mb-0">{{ fileName }}</h4>
      </div>
      
      <div class="header-right">
        <div class="hint-text" v-if="!isCodeMode">
          <i class="fa fa-info-circle"></i>
          Hold <kbd>Ctrl/Cmd</kbd> + Click to select elements
        </div>
        
        <b-button-group class="mr-2">
          <b-button 
            :variant="!isCodeMode ? 'primary' : 'outline-secondary'" 
            size="sm" 
            @click="switchToVisualMode"
          >
            <i class="fa fa-eye"></i> Visual
          </b-button>
          <b-button 
            :variant="isCodeMode ? 'primary' : 'outline-secondary'" 
            size="sm" 
            @click="switchToCodeMode"
          >
            <i class="fa fa-code"></i> Code
          </b-button>
        </b-button-group>
        
        <b-button 
          variant="success" 
          size="sm" 
          @click="saveHtml"
          :disabled="saving"
        >
          <b-spinner small v-if="saving" />
          <template v-else>
            <i class="fa fa-save"></i> Save
          </template>
        </b-button>
      </div>
    </div>

    <div class="editor-toolbar" v-if="!isCodeMode">
      <b-button-group class="mr-2">
        <b-button
          @click="execCommand('formatBlock', '<h1>')"
          variant="outline-secondary"
          size="sm"
        >
          H1
        </b-button>
        <b-button
          @click="execCommand('formatBlock', '<h2>')"
          variant="outline-secondary"
          size="sm"
        >
          H2
        </b-button>
        <b-button
          @click="execCommand('formatBlock', '<h3>')"
          variant="outline-secondary"
          size="sm"
        >
          H3
        </b-button>
        <b-button
          @click="execCommand('formatBlock', '<p>')"
          variant="outline-secondary"
          size="sm"
        >
          P
        </b-button>
      </b-button-group>

      <b-button-group class="mr-2">
        <b-button
          @click="execCommand('bold')"
          variant="outline-secondary"
          size="sm"
        >
          <i class="fa fa-bold"></i>
        </b-button>
        <b-button
          @click="execCommand('italic')"
          variant="outline-secondary"
          size="sm"
        >
          <i class="fa fa-italic"></i>
        </b-button>
        <b-button
          @click="execCommand('underline')"
          variant="outline-secondary"
          size="sm"
        >
          <i class="fa fa-underline"></i>
        </b-button>
        <b-button
          @click="execCommand('strikeThrough')"
          variant="outline-secondary"
          size="sm"
        >
          <i class="fa fa-strikethrough"></i>
        </b-button>
      </b-button-group>

      <b-button-group class="mr-2">
        <b-button
          @click="execCommand('insertUnorderedList')"
          variant="outline-secondary"
          size="sm"
        >
          <i class="fa fa-list-ul"></i>
        </b-button>
        <b-button
          @click="execCommand('insertOrderedList')"
          variant="outline-secondary"
          size="sm"
        >
          <i class="fa fa-list-ol"></i>
        </b-button>
      </b-button-group>

      <b-button-group class="mr-2">
        <b-button
          @click="execCommand('justifyLeft')"
          variant="outline-secondary"
          size="sm"
        >
          <i class="fa fa-align-left"></i>
        </b-button>
        <b-button
          @click="execCommand('justifyCenter')"
          variant="outline-secondary"
          size="sm"
        >
          <i class="fa fa-align-center"></i>
        </b-button>
        <b-button
          @click="execCommand('justifyRight')"
          variant="outline-secondary"
          size="sm"
        >
          <i class="fa fa-align-right"></i>
        </b-button>
      </b-button-group>

      <b-button
        variant="outline-secondary"
        size="sm"
        @click="insertLink"
        class="mr-2"
      >
        <i class="fa fa-link"></i>
      </b-button>

      <b-button
        variant="outline-secondary"
        size="sm"
        @click="insertImage"
        class="mr-2"
      >
        <i class="fa fa-image"></i>
      </b-button>
    </div>

    <div class="editor-container">
      <!-- Visual Editor -->
      <iframe
        v-if="!isCodeMode"
        ref="editorFrame"
        class="editor-iframe"
        @load="onIframeLoad"
      ></iframe>
      
      <!-- Code Editor -->
      <MonacoEditor
        v-if="isCodeMode"
        v-model="codeContent"
        language="html"
        :height="editorHeight"
        :options="monacoOptions"
        class="code-editor"
      />
      
      <!-- Element Inspector Panel -->
      <div v-if="selectedElement && !isCodeMode" class="inspector-panel">
        <div class="inspector-header">
          <h5>Element Inspector</h5>
          <b-button size="sm" variant="link" @click="clearSelection">
            <i class="fa fa-times"></i>
          </b-button>
        </div>
        
        <div class="inspector-content">
          <div class="inspector-section">
            <label class="inspector-label">Element Type</label>
            <div class="inspector-value">{{ selectedElementTag }}</div>
          </div>
          
          <div class="inspector-section">
            <label class="inspector-label">Classes</label>
            <input
              v-model="selectedElementClasses"
              @change="updateElementClasses"
              class="form-control form-control-sm"
              placeholder="class1 class2"
            />
          </div>
          
          <div class="inspector-section">
            <label class="inspector-label">ID</label>
            <input
              v-model="selectedElementId"
              @change="updateElementId"
              class="form-control form-control-sm"
              placeholder="element-id"
            />
          </div>
          
          <div class="inspector-section">
            <h6>CSS Properties</h6>
            <div class="css-properties">
              <div v-for="(value, prop) in commonCssProperties" :key="prop" class="css-property">
                <label>{{ prop }}</label>
                <input
                  v-model="cssProperties[prop]"
                  @input="updateCssProperty(prop)"
                  class="form-control form-control-sm"
                  :placeholder="value"
                />
              </div>
            </div>
          </div>
          
          <div class="inspector-section">
            <h6>Custom CSS</h6>
            <textarea
              v-model="customCss"
              @input="updateCustomCss"
              class="form-control form-control-sm custom-css-editor"
              placeholder="property: value;&#10;another-property: value;"
              rows="6"
            ></textarea>
            <small class="text-muted">Enter CSS properties (e.g., border-radius: 8px;)</small>
          </div>
          
          <div class="inspector-section">
            <h6>Inline Style</h6>
            <textarea
              v-model="inlineStyle"
              @change="applyInlineStyle"
              class="form-control form-control-sm"
              placeholder="Complete inline style attribute"
              rows="4"
              readonly
            ></textarea>
            <small class="text-muted">Current inline styles on the element</small>
          </div>
          
          <div class="inspector-section">
            <b-button size="sm" variant="danger" @click="deleteElement" block>
              <i class="fa fa-trash"></i> Delete Element
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MonacoEditor from '@/components/form/Editor/MonacoEditor.vue';

export default {
  name: "HtmlWysiwygEditor",
  components: {
    MonacoEditor
  },
  data() {
    return {
      fileName: "",
      fileKey: "",
      saving: false,
      htmlContent: "",
      iframeReady: false,
      selectedElement: null,
      selectedElementTag: "",
      selectedElementClasses: "",
      selectedElementId: "",
      cssProperties: {},
      customCss: "",
      inlineStyle: "",
      commonCssProperties: {
        'background-color': '',
        'color': '',
        'font-size': '',
        'font-weight': '',
        'margin': '',
        'padding': '',
        'border': '',
        'width': '',
        'height': '',
        'display': '',
        'text-align': '',
      },
      draggedElement: null,
      isCodeMode: false,
      codeContent: "",
      editorHeight: 600,
      monacoOptions: {
        automaticLayout: true,
        formatOnPaste: true,
        formatOnType: true,
        minimap: { enabled: true },
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        theme: 'vs-dark'
      }
    };
  },
  mounted() {
    this.fileName = this.$route.query.name || "Untitled.html";
    
    // Construct the full path key with extension
    const path = this.$route.query.path || '';
    this.fileKey = path ? `${path}/${this.fileName}` : this.fileName;
    
    this.loadHtmlContent();
    this.calculateEditorHeight();
    
    window.addEventListener('resize', this.calculateEditorHeight);

    if (!window.kortex) return
    var self = this

    window.kortex.registerContext(
      'HTML Editor',
      'BEFORE YOU DO ANYTHING ELSE: first search the internal knowledge base for relevant topics. ' +
      'The user is editing an HTML file named "' + this.fileName + '". ' +
      'You can read the raw HTML source, replace the full HTML content or the code-editor content, save the file, and toggle between visual and code modes.',
      'fa-code'
    )
    window.kortex.showHelper()

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_html_file_info',
          description: 'Returns the current HTML file name, path/key, and whether the editor is in code or visual mode.',
          parameters: { type: 'object', properties: {} }
        }
      },
      function () {
        return {
          file_name: self.fileName,
          file_key: self.fileKey,
          mode: self.isCodeMode ? 'code' : 'visual'
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_html_content',
          description: 'Returns the full HTML source of the file. In visual mode reads from the live iframe; in code mode reads the Monaco editor buffer.',
          parameters: { type: 'object', properties: {} }
        }
      },
      function () {
        var html = self.isCodeMode ? self.codeContent : self.getIframeContent()
        return { content: html, mode: self.isCodeMode ? 'code' : 'visual' }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'set_html_content',
          description: 'Replaces the entire HTML content in the editor. Works in both visual and code modes.',
          parameters: {
            type: 'object',
            required: ['content'],
            properties: {
              content: { type: 'string', description: 'The full HTML to put in the editor.' }
            }
          }
        }
      },
      function (args) {
        if (!args.content && args.content !== '') return { error: 'content is required.' }
        if (self.isCodeMode) {
          self.codeContent = args.content
        } else {
          self.htmlContent = args.content
          self.codeContent = args.content
          self.$nextTick(function () {
            if (self.iframeReady) self.setIframeContent()
          })
        }
        return { ok: true, length: args.content.length }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'switch_html_mode',
          description: 'Switches the HTML editor between visual (iframe) and code (Monaco) modes.',
          parameters: {
            type: 'object',
            required: ['mode'],
            properties: {
              mode: { type: 'string', enum: ['visual', 'code'], description: '"visual" for the WYSIWYG view, "code" for the Monaco code editor.' }
            }
          }
        }
      },
      function (args) {
        if (args.mode === 'code') { self.switchToCodeMode() }
        else if (args.mode === 'visual') { self.switchToVisualMode() }
        else { return { error: 'mode must be "visual" or "code".' } }
        return { mode: self.isCodeMode ? 'code' : 'visual' }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'save_html_file',
          description: 'Saves the current HTML content to storage.',
          parameters: { type: 'object', properties: {} }
        }
      },
      function () {
        return self.saveHtml().then(function () {
          return { saved: true, file: self.fileName }
        }).catch(function (e) {
          return { error: String(e.message || e) }
        })
      }
    )
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateEditorHeight);
    if (window.kortex) {
      window.kortex.unregisterContext()
      window.kortex.hideHelper()
      window.kortex.unregisterTool('get_html_file_info')
      window.kortex.unregisterTool('get_html_content')
      window.kortex.unregisterTool('set_html_content')
      window.kortex.unregisterTool('switch_html_mode')
      window.kortex.unregisterTool('save_html_file')
    }
  },
  methods: {
    calculateEditorHeight() {
      // Calculate height: window height - header - toolbar - padding
      const windowHeight = window.innerHeight;
      const headerHeight = 60; // approximate header height
      const toolbarHeight = this.isCodeMode ? 0 : 50; // toolbar only in visual mode
      const padding = 40;
      this.editorHeight = windowHeight - headerHeight - toolbarHeight - padding;
    },
    
    switchToVisualMode() {
      if (this.isCodeMode) {
        // Save code content back to htmlContent
        this.htmlContent = this.codeContent;
        this.isCodeMode = false;
        
        // Update iframe content
        this.$nextTick(() => {
          if (this.iframeReady) {
            this.setIframeContent();
          }
        });
      }
      this.calculateEditorHeight();
    },
    
    switchToCodeMode() {
      if (!this.isCodeMode) {
        // Get current content from iframe
        const content = this.getIframeContent();
        this.codeContent = content;
        this.isCodeMode = true;
      }
      this.calculateEditorHeight();
    },
    
    async loadHtmlContent() {
      try {
        const response = await this.$mediaHttp.get(`/v3/dms/media/get/${this.fileKey}`, {
          responseType: 'text'
        });
        
        this.htmlContent = response.data;
        this.codeContent = response.data; // Also set code content
        
        // If iframe is already loaded, update content
        if (this.iframeReady) {
          this.setIframeContent();
        }
      } catch (error) {
        console.error("Failed to load HTML content:", error);
        this.$bvToast.toast('Failed to load file', {
          title: 'Error',
          variant: 'danger'
        });
      }
    },
    
    onIframeLoad() {
      this.iframeReady = true;
      if (this.htmlContent) {
        this.setIframeContent();
      }
    },
    
    setIframeContent() {
      const iframe = this.$refs.editorFrame;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      
      // Write the HTML content to the iframe
      iframeDoc.open();
      iframeDoc.write(this.htmlContent);
      iframeDoc.close();
      
      // Make the content editable
      iframeDoc.body.contentEditable = true;
      iframeDoc.designMode = 'on';
      
      // Add styles for element selection and highlighting
      const style = iframeDoc.createElement('style');
      style.textContent = `
        body {
          margin: 0;
          padding: 20px;
        }
        body:focus {
          outline: none;
        }
        .wysiwyg-selected {
          outline: 2px solid var(--primary-start) !important;
          outline-offset: 2px;
          cursor: move !important;
        }
        .wysiwyg-hover {
          outline: 1px dashed var(--primary-start) !important;
          outline-offset: 1px;
        }
        * {
          transition: outline 0.1s;
        }
      `;
      iframeDoc.head.appendChild(style);
      
      // Add event listeners for element selection and drag & drop
      this.setupElementInteraction(iframeDoc);
    },
    
    setupElementInteraction(iframeDoc) {
      // Click to select elements
      iframeDoc.addEventListener('click', (e) => {
        // Allow ctrl/cmd + click to select elements
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          e.stopPropagation();
          this.selectElement(e.target);
        }
      });
      
      // Hover effect
      iframeDoc.addEventListener('mouseover', (e) => {
        if (e.ctrlKey || e.metaKey) {
          e.target.classList.add('wysiwyg-hover');
        }
      });
      
      iframeDoc.addEventListener('mouseout', (e) => {
        e.target.classList.remove('wysiwyg-hover');
      });
      
      // Enable drag and drop
      this.setupDragAndDrop(iframeDoc);
    },
    
    setupDragAndDrop(iframeDoc) {
      iframeDoc.addEventListener('dragstart', (e) => {
        if (this.selectedElement && e.target === this.selectedElement) {
          this.draggedElement = e.target;
          e.dataTransfer.effectAllowed = 'move';
          e.target.style.opacity = '0.5';
        }
      });
      
      iframeDoc.addEventListener('dragend', (e) => {
        if (e.target === this.draggedElement) {
          e.target.style.opacity = '';
        }
      });
      
      iframeDoc.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      });
      
      iframeDoc.addEventListener('drop', (e) => {
        e.preventDefault();
        
        if (this.draggedElement && e.target !== this.draggedElement) {
          const dropTarget = e.target;
          
          // Insert before or after based on mouse position
          if (dropTarget.parentNode) {
            const rect = dropTarget.getBoundingClientRect();
            const midpoint = rect.top + rect.height / 2;
            
            if (e.clientY < midpoint) {
              dropTarget.parentNode.insertBefore(this.draggedElement, dropTarget);
            } else {
              dropTarget.parentNode.insertBefore(this.draggedElement, dropTarget.nextSibling);
            }
          }
        }
      });
    },
    
    selectElement(element) {
      const iframe = this.$refs.editorFrame;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      
      // Clear previous selection
      const previousSelected = iframeDoc.querySelector('.wysiwyg-selected');
      if (previousSelected) {
        previousSelected.classList.remove('wysiwyg-selected');
        previousSelected.removeAttribute('draggable');
      }
      
      // Set new selection
      this.selectedElement = element;
      element.classList.add('wysiwyg-selected');
      element.setAttribute('draggable', 'true');
      
      // Get element info
      this.selectedElementTag = element.tagName.toLowerCase();
      this.selectedElementClasses = element.className.replace('wysiwyg-selected', '').trim();
      this.selectedElementId = element.id || '';
      
      // Get computed styles
      const computedStyle = iframe.contentWindow.getComputedStyle(element);
      this.cssProperties = {};
      
      Object.keys(this.commonCssProperties).forEach(prop => {
        this.cssProperties[prop] = computedStyle.getPropertyValue(prop);
      });
      
      // Get inline styles
      this.inlineStyle = element.getAttribute('style') || '';
      this.customCss = '';
    },
    
    clearSelection() {
      if (this.selectedElement) {
        this.selectedElement.classList.remove('wysiwyg-selected');
        this.selectedElement.removeAttribute('draggable');
        this.selectedElement = null;
      }
    },
    
    updateElementClasses() {
      if (this.selectedElement) {
        this.selectedElement.className = this.selectedElementClasses + ' wysiwyg-selected';
      }
    },
    
    updateElementId() {
      if (this.selectedElement) {
        this.selectedElement.id = this.selectedElementId;
      }
    },
    
    updateCssProperty(prop) {
      if (this.selectedElement) {
        this.selectedElement.style[prop] = this.cssProperties[prop];
        this.updateInlineStyleDisplay();
      }
    },
    
    updateCustomCss() {
      if (!this.selectedElement) return;
      
      // Parse the custom CSS and apply it
      const cssLines = this.customCss.split(';').filter(line => line.trim());
      
      cssLines.forEach(line => {
        const [property, value] = line.split(':').map(s => s.trim());
        if (property && value) {
          this.selectedElement.style.setProperty(property, value);
        }
      });
      
      this.updateInlineStyleDisplay();
    },
    
    updateInlineStyleDisplay() {
      if (this.selectedElement) {
        this.inlineStyle = this.selectedElement.getAttribute('style') || '';
      }
    },
    
    applyInlineStyle() {
      if (this.selectedElement) {
        this.selectedElement.setAttribute('style', this.inlineStyle);
      }
    },
    
    deleteElement() {
      if (this.selectedElement) {
        const confirmed = confirm('Are you sure you want to delete this element?');
        if (confirmed) {
          this.selectedElement.remove();
          this.clearSelection();
        }
      }
    },
    
    execCommand(command, value = null) {
      const iframe = this.$refs.editorFrame;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      
      iframe.contentWindow.focus();
      iframeDoc.execCommand(command, false, value);
    },
    
    insertLink() {
      const url = window.prompt("Enter URL:");
      if (url) {
        this.execCommand('createLink', url);
      }
    },
    
    insertImage() {
      const url = window.prompt("Enter image URL:");
      if (url) {
        this.execCommand('insertImage', url);
      }
    },
    
    getIframeContent() {
      const iframe = this.$refs.editorFrame;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      
      // Clone the document to avoid modifying the displayed version
      const clonedDoc = iframeDoc.documentElement.cloneNode(true);
      
      // Remove all wysiwyg helper classes
      const elementsWithWysiwygClasses = clonedDoc.querySelectorAll('.wysiwyg-selected, .wysiwyg-hover');
      elementsWithWysiwygClasses.forEach(element => {
        element.classList.remove('wysiwyg-selected', 'wysiwyg-hover');
        element.removeAttribute('draggable');
      });
      
      // Remove the wysiwyg helper styles that were injected
      const wysiwygStyles = clonedDoc.querySelectorAll('style');
      wysiwygStyles.forEach(styleEl => {
        if (styleEl.textContent.includes('wysiwyg-selected') || styleEl.textContent.includes('wysiwyg-hover')) {
          styleEl.remove();
        }
      });
      
      // Get the full HTML including doctype
      return '<!DOCTYPE html>\n' + clonedDoc.outerHTML;
    },
    
    async saveHtml() {
      this.saving = true;
      try {
        let htmlContent;
        
        // Get content from current editor mode
        if (this.isCodeMode) {
          htmlContent = this.codeContent;
        } else {
          htmlContent = this.getIframeContent();
        }
        
        // Convert HTML content to base64
        const base64Content = btoa(unescape(encodeURIComponent(htmlContent)));
        
        // Prepare the request payload
        const payload = {
          file: {
            name: this.fileName,
            content_type: "text/html",
            data: base64Content
          }
        };
        
        // Update the file using the media key
        await this.$mediaHttp.put(
          `/v3/dms/media/${this.fileKey}`,
          payload
        );
      } catch (error) {
        console.error("Failed to save HTML:", error);
        this.$bvToast.toast('Failed to save file', {
          title: 'Error',
          variant: 'danger'
        });
      } finally {
        this.saving = false;
      }
    },
    
    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss" scoped>
/* Dark theme color variables */
$dark-background: var(--bg-card);
$dark-hard-background: var(--form-bg);
$dark-border: var(--border-hover);
$dark-hard2-background: var(--bg-card);
$dark-border2: var(--border-default);
$protokol-royal-active: var(--primary-start);

.html-wysiwyg-editor {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: $dark-hard2-background;
}

.editor-header {
  background: $dark-hard-background;
  border-bottom: 1px solid $dark-border2;
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-left h4 {
  color: var(--text-primary);
}

.back-button {
  color: var(--text-primary);
  padding: 4px 8px;
}

.back-button:hover {
  color: $protokol-royal-active;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.hint-text {
  color: #ccc;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  kbd {
    background: $dark-hard2-background;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 11px;
    border: 1px solid $dark-border2;
  }
}

.editor-toolbar {
  background: $dark-hard2-background;
  border-bottom: 1px solid $dark-border2;
  padding: 8px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.editor-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  background: var(--bg-card);
  position: relative;
}

.editor-iframe {
  flex: 1;
  width: 100%;
  height: 100%;
  border: none;
  background: var(--bg-card);
}

.code-editor {
  flex: 1;
  width: 100%;
  height: 100%;
}

.inspector-panel {
  width: 320px;
  background: $dark-background;
  border-left: 1px solid $dark-border2;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.inspector-header {
  background: $dark-hard-background;
  padding: 12px 16px;
  border-bottom: 1px solid $dark-border2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h5 {
    margin: 0;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
  }
  
  .btn-link {
    color: var(--text-primary);
    padding: 0;
    
    &:hover {
      color: $protokol-royal-active;
    }
  }
}

.inspector-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.inspector-section {
  margin-bottom: 20px;
  
  h6 {
    color: var(--text-primary);
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: 12px;
  }
}

.inspector-label {
  display: block;
  color: #ccc;
  font-size: 12px;
  margin-bottom: 6px;
}

.inspector-value {
  color: var(--text-primary);
  font-size: 14px;
  padding: 6px 0;
  font-family: monospace;
}

.css-properties {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.css-property {
  label {
    display: block;
    color: #ccc;
    font-size: 11px;
    margin-bottom: 4px;
    font-family: monospace;
  }
  
  input {
    font-family: monospace;
    font-size: 12px;
  }
}

.custom-css-editor {
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 12px;
  line-height: 1.5;
  resize: vertical;
}

.text-muted {
  color: #999;
  font-size: 11px;
  display: block;
  margin-top: 6px;
}
</style>

